/**
 * Client API pour le module Directory (Gateway) utilisé par le FRONTEND
 * Endpoints d'agrégation pour la navigation hiérarchique
 */

import { httpClient } from './http.client'
import {
  ClassMemberWithUser,
  EstablishmentMemberWithUser,
  GroupMemberWithUser,
} from '../dto/directory.dto'
const BASE_PATH = '/directory'

// ============================================================================
// Client
// ============================================================================

export const directoryClient = {
  /**
   * Liste les membres d'un établissement avec leurs infos utilisateur
   * Endpoint d'agrégation Gateway: School Service + User Service
   */
  async getEstablishmentMembers(
    establishmentId: string
  ): Promise<EstablishmentMemberWithUser[]> {
    const { data } = await httpClient.get<EstablishmentMemberWithUser[]>(
      `${BASE_PATH}/establishments/${establishmentId}/members`
    )
    return data
  },

  /**
   * Liste les élèves assignables à une classe (rôle STUDENT)
   * @param filters.classId — exclut les élèves déjà membres de cette classe (ClassMembership)
   */
  async getAssignableStudents(
    establishmentId: string,
    filters?: { classId?: string }
  ): Promise<EstablishmentMemberWithUser[]> {
    const { data } = await httpClient.get<EstablishmentMemberWithUser[]>(
      `${BASE_PATH}/establishments/${establishmentId}/assignable-students`,
      {
        params: filters?.classId ? { classId: filters.classId } : undefined,
      }
    )
    return data
  },

  /**
   * Liste les enseignants (rôle TEACHER)
   * @param filters.classId avec classTeachersOnly — uniquement enseignants avec ClassMembership dans la classe
   * @param filters.classId sans classTeachersOnly — hors membres déjà dans la classe (ajout membre)
   */
  async getAssignableTeachers(
    establishmentId: string,
    filters?: { classId?: string; classTeachersOnly?: boolean }
  ): Promise<EstablishmentMemberWithUser[]> {
    const params: Record<string, string> = {}
    if (filters?.classId) {
      params['classId'] = filters.classId
    }
    if (filters?.classTeachersOnly && filters.classId) {
      params['classTeachersOnly'] = 'true'
    }
    const { data } = await httpClient.get<EstablishmentMemberWithUser[]>(
      `${BASE_PATH}/establishments/${establishmentId}/assignable-teachers`,
      Object.keys(params).length > 0 ? { params } : undefined
    )
    return data
  },

  /**
   * Liste les membres d'une classe avec leurs infos utilisateur
   * Endpoint d'agrégation Gateway: School Service + User Service
   */
  async getClassMembers(classId: string): Promise<ClassMemberWithUser[]> {
    const { data } = await httpClient.get<ClassMemberWithUser[]>(
      `${BASE_PATH}/classes/${classId}/members`
    )
    return data
  },

  /**
   * Liste les membres d'un groupe avec leurs infos utilisateur
   * Endpoint d'agrégation Gateway: School Service + User Service
   */
  async getGroupMembers(groupId: string): Promise<GroupMemberWithUser[]> {
    const { data } = await httpClient.get<GroupMemberWithUser[]>(
      `${BASE_PATH}/groups/${groupId}/members`
    )
    return data
  },
}

export default directoryClient

